import React, { memo } from 'react';

// Interface para Skeleton Loader
interface LoadingSkeletonProps {
  lines?: number;
  height?: string;
  className?: string;
  animated?: boolean;
}

// Interface para Loading Card
interface LoadingCardProps {
  title?: boolean;
  subtitle?: boolean;
  content?: boolean;
  actions?: boolean;
  className?: string;
}

// Interface para Loading Table
interface LoadingTableProps {
  rows?: number;
  columns?: number;
  className?: string;
}

// Interface para Loading Button
interface LoadingButtonProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  className?: string;
}

// Interface para Loading Spinner
interface LoadingSpinnerProps {
  size?: number;
  color?: string;
  className?: string;
}

/**
 * Componente Skeleton Loader para substituir spinners genéricos
 */
export const LoadingSkeleton = memo(({
  lines = 3,
  height = '20px',
  className = '',
  animated = true
}: LoadingSkeletonProps) => {
  const baseClasses = `bg-gray-200 dark:bg-gray-700 rounded ${animated ? 'animate-pulse' : ''}`;
  
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={baseClasses}
          style={{ 
            height,
            width: index === lines - 1 ? '75%' : '100%' // Última linha menor para parecer mais natural
          }}
        />
      ))}
    </div>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

/**
 * Componente Loading Card específico para cards
 */
export const LoadingCard = memo(({
  title = true,
  subtitle = false,
  content = true,
  actions = false,
  className = ''
}: LoadingCardProps) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 ${className}`}>
      {title && (
        <div className="bg-gray-200 dark:bg-gray-700 h-6 w-3/4 rounded animate-pulse mb-4" />
      )}
      
      {subtitle && (
        <div className="bg-gray-200 dark:bg-gray-700 h-4 w-1/2 rounded animate-pulse mb-4" />
      )}
      
      {content && (
        <div className="space-y-3">
          <div className="bg-gray-200 dark:bg-gray-700 h-4 w-full rounded animate-pulse" />
          <div className="bg-gray-200 dark:bg-gray-700 h-4 w-5/6 rounded animate-pulse" />
          <div className="bg-gray-200 dark:bg-gray-700 h-4 w-4/6 rounded animate-pulse" />
        </div>
      )}
      
      {actions && (
        <div className="flex space-x-2 mt-6">
          <div className="bg-gray-200 dark:bg-gray-700 h-10 w-20 rounded animate-pulse" />
          <div className="bg-gray-200 dark:bg-gray-700 h-10 w-16 rounded animate-pulse" />
        </div>
      )}
    </div>
  );
});

LoadingCard.displayName = 'LoadingCard';

/**
 * Componente Loading Table para tabelas
 */
export const LoadingTable = memo(({
  rows = 5,
  columns = 4,
  className = ''
}: LoadingTableProps) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-b border-gray-200 dark:border-gray-600">
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, index) => (
            <div
              key={`header-${index}`}
              className="bg-gray-300 dark:bg-gray-600 h-4 rounded animate-pulse"
              style={{ width: `${100 / columns}%` }}
            />
          ))}
        </div>
      </div>
      
      {/* Rows */}
      <div className="divide-y divide-gray-200 dark:divide-gray-600">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="px-6 py-4">
            <div className="flex space-x-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div
                  key={`cell-${rowIndex}-${colIndex}`}
                  className="bg-gray-200 dark:bg-gray-700 h-4 rounded animate-pulse"
                  style={{ width: `${100 / columns}%` }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

LoadingTable.displayName = 'LoadingTable';

/**
 * Componente Loading Button para botões em estado de carregamento
 */
export const LoadingButton = memo(({
  size = 'md',
  variant = 'primary',
  className = ''
}: LoadingButtonProps) => {
  const sizeClasses = {
    sm: 'h-8 w-16',
    md: 'h-10 w-20',
    lg: 'h-12 w-24'
  };

  const variantClasses = {
    primary: 'bg-blue-200 dark:bg-blue-800',
    secondary: 'bg-gray-200 dark:bg-gray-700',
    danger: 'bg-red-200 dark:bg-red-800',
    success: 'bg-green-200 dark:bg-green-800',
    warning: 'bg-yellow-200 dark:bg-yellow-800'
  };

  return (
    <div
      className={`
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        rounded
        animate-pulse
        ${className}
      `}
    />
  );
});

LoadingButton.displayName = 'LoadingButton';

/**
 * Componente Loading Spinner customizável
 */
export const LoadingSpinner = memo(({
  size = 40,
  color = 'blue',
  className = ''
}: LoadingSpinnerProps) => {
  const colorClasses = {
    blue: 'border-blue-500',
    gray: 'border-gray-500',
    green: 'border-green-500',
    red: 'border-red-500',
    yellow: 'border-yellow-500'
  };

  return (
    <div className={`flex justify-center items-center ${className}`}>
      <div
        className={`
          animate-spin
          rounded-full
          border-4
          border-gray-200
          ${colorClasses[color as keyof typeof colorClasses] || colorClasses.blue}
          border-t-transparent
        `}
        style={{
          width: `${size}px`,
          height: `${size}px`
        }}
      />
    </div>
  );
});

LoadingSpinner.displayName = 'LoadingSpinner';

/**
 * Componente Loading Dashboard específico para dashboard
 */
export const LoadingDashboard = memo(() => {
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <LoadingCard
            key={`stat-card-${index}`}
            title={true}
            subtitle={false}
            content={false}
            actions={false}
            className="h-32"
          />
        ))}
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart Area */}
        <LoadingCard
          title={true}
          subtitle={false}
          content={true}
          actions={false}
          className="h-96"
        />
        
        {/* Map Area */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 h-96">
          <div className="bg-gray-200 dark:bg-gray-700 h-6 w-1/3 rounded animate-pulse mb-4" />
          <div className="bg-gray-300 dark:bg-gray-600 h-full rounded animate-pulse" />
        </div>
      </div>
      
      {/* Table Area */}
      <LoadingTable rows={8} columns={5} />
    </div>
  );
});

LoadingDashboard.displayName = 'LoadingDashboard';

/**
 * Hook para controle de loading states contextuais
 */
export const useLoadingStates = () => {
  const [loadingStates, setLoadingStates] = React.useState<Record<string, boolean>>({});

  const setLoading = React.useCallback((key: string, loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: loading
    }));
  }, []);

  const isLoading = React.useCallback((key: string) => {
    return loadingStates[key] || false;
  }, [loadingStates]);

  const isAnyLoading = React.useCallback(() => {
    return Object.values(loadingStates).some(loading => loading);
  }, [loadingStates]);

  const clearAll = React.useCallback(() => {
    setLoadingStates({});
  }, []);

  return {
    setLoading,
    isLoading,
    isAnyLoading,
    clearAll,
    loadingStates
  };
};

export default {
  LoadingSkeleton,
  LoadingCard,
  LoadingTable,
  LoadingButton,
  LoadingSpinner,
  LoadingDashboard,
  useLoadingStates
};